import { useEffect, useState } from 'react';
import {
  Users, Search, ChevronLeft, Mail, CreditCard, FileText, ShieldCheck, AlertCircle, CheckCircle2, ExternalLink, Send, RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { customerPortalApi, hasUsableEmail, type CustomerRow, type CustomerDetail } from '../../api/customerPortal';
import { useWorkspace } from '../../hooks/useWorkspace';
import { formatDate } from '../../utils/formatTime';
import { cardOpen } from '../../utils/cardOpen';

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function CustomerList({ onOpen }: { onOpen: (id: string) => void }) {
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = async (q?: string) => {
    setLoading(true); setError('');
    try {
      setRows(await customerPortalApi.listCustomers(q));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not load customers.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users size={18} className="text-slate-500" />
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Customers</h2>
          <p className="text-sm text-slate-500">{rows.length} customer{rows.length === 1 ? '' : 's'}.</p>
        </div>
      </div>

      <form onSubmit={e => { e.preventDefault(); void load(search); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email"
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100" />
        </div>
      </form>

      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}

      {loading ? (
        <div className="flex h-32 items-center justify-center"><LoadingSpinner size="md" /></div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.length === 0 && <p className="text-sm text-slate-500">No customers found.</p>}
          {rows.map(c => (
            <Card key={c.id} className="cursor-pointer p-4" {...cardOpen({ agentId: `portal.customers.card.${c.id}`, label: `Open customer ${c.username || c.email || ''}`, onOpen: () => onOpen(c.id) })}>
              <button onClick={(e) => { e.stopPropagation(); onOpen(c.id); }} className="w-full text-left">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-950 dark:text-slate-100">{c.username || '—'}</div>
                    <div className="truncate text-xs text-slate-500">{c.email}</div>
                  </div>
                  {c.is_owner && <span className="shrink-0 rounded-full bg-brand-500/10 px-2 py-0.5 text-[11px] font-medium text-brand-600 dark:text-brand-300">Owner</span>}
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-sm font-semibold text-slate-950 dark:text-slate-100">{c.active_subscriptions}</div><div className="text-[11px] text-slate-500">Active</div></div>
                  <div><div className="text-sm font-semibold text-slate-950 dark:text-slate-100">{c.invoices}</div><div className="text-[11px] text-slate-500">Invoices</div></div>
                  <div><div className="text-sm font-semibold text-slate-950 dark:text-slate-100">{money(c.lifetime_value_cents)}</div><div className="text-[11px] text-slate-500">LTV</div></div>
                </div>
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerDetailView({ id, onBack }: { id: string; onBack: () => void }) {
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    customerPortalApi.customerDetail(id)
      .then(d => { if (alive) setDetail(d); })
      .catch((err: any) => { if (alive) setError(err?.response?.data?.detail || 'Could not load customer.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;
  if (error) return <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400">{error}</div>;
  if (!detail) return null;

  const sendLink = async () => {
    if (!hasUsableEmail(detail.email)) return;
    setSending(true); setError(''); setActionError('');
    try {
      const res = await customerPortalApi.sendSupportLink(id);
      setToast(`Support link emailed to ${res.emailed_to}`);
      window.setTimeout(() => setToast(''), 4500);
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || 'Could not send the support link.');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-5">
      {toast && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 shadow-lg dark:border-green-800 dark:bg-green-900/40 dark:text-green-300">
          <Mail size={16} />{toast}
        </div>
      )}
      {actionError && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{actionError}</div>}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <ChevronLeft size={15} />Back to customers
      </button>

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-100">{detail.username || '—'}</h2>
            <p className="text-sm text-slate-500">{detail.email}</p>
            <p className="mt-1 text-xs text-slate-400">Customer since {detail.created_at ? formatDate(detail.created_at) : '—'}</p>
          </div>
          <div className="rounded-lg border border-slate-200 px-4 py-3 text-right dark:border-slate-700">
            <div className="text-xl font-bold text-slate-950 dark:text-slate-100">{money(detail.lifetime_value_cents)}</div>
            <div className="text-xs text-slate-500">Lifetime value</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={`mailto:${detail.email}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
            <Mail size={14} />Email customer
          </a>
          <Button
            size="sm"
            variant="secondary"
            disabled={!hasUsableEmail(detail.email) || sending}
            title={hasUsableEmail(detail.email) ? `Email a one-time support link to ${detail.email}` : 'No deliverable email on file — add a real email first'}
            onClick={() => void sendLink()}
          >
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            Send support link
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2"><CreditCard size={16} className="text-slate-500" /><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Subscriptions</h3></div>
        {detail.subscriptions.length === 0 ? <p className="text-sm text-slate-500">No subscriptions.</p> : (
          <div className="space-y-1">
            {detail.subscriptions.map(s => (
              <div key={s.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="text-sm text-slate-700 dark:text-slate-300">{s.plan_name}</span>
                <span className="text-xs capitalize text-slate-500">{s.status}{s.current_period_end ? ` · renews ${formatDate(s.current_period_end)}` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2"><FileText size={16} className="text-slate-500" /><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Invoices</h3></div>
        {detail.invoices.length === 0 ? <p className="text-sm text-slate-500">No invoices.</p> : (
          <div className="space-y-1">
            {detail.invoices.map(i => (
              <div key={i.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{i.number}</span>
                <span className="text-sm text-slate-950 dark:text-slate-100">{money(i.amount_cents)} <span className="text-xs capitalize text-slate-500">({i.status})</span></span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-slate-500" /><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Payment Details</h3></div>
        {detail.payment_methods.length === 0 ? (
          <p className="text-sm text-slate-500">No saved payment methods.</p>
        ) : (
          <div className="space-y-1">
            {detail.payment_methods.map(m => (
              <div key={m.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="text-sm capitalize text-slate-700 dark:text-slate-300">{m.brand || m.provider} ···· {m.last4 || '----'}</span>
                <span className="text-xs text-slate-500">{m.exp_month && m.exp_year ? `${m.exp_month}/${m.exp_year}` : ''}{m.is_default ? ' · default' : ''}</span>
              </div>
            ))}
          </div>
        )}
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" />{detail.payment_methods_note}
        </p>
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2"><CreditCard size={16} className="text-slate-500" /><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">Recent Payments</h3></div>
        {detail.payments.length === 0 ? <p className="text-sm text-slate-500">No payments recorded.</p> : (
          <div className="space-y-1">
            {detail.payments.map(p => (
              <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
                <span className="text-xs text-slate-500">{p.event_type}</span>
                <span className="text-sm text-slate-950 dark:text-slate-100">{p.amount_cents != null ? money(p.amount_cents) : '—'}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function PeopleSettings() {
  const { currentWorkspace } = useWorkspace();
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center gap-2"><Users size={18} className="text-slate-500" /><h3 className="text-base font-semibold text-slate-950 dark:text-slate-100">People &amp; Workspace Access</h3></div>
      <p className="text-sm text-slate-500">
        Manage who belongs to{currentWorkspace ? ` "${currentWorkspace.name}"` : ' this workspace'}, their roles, and access.
        This is the former People settings, now part of the Customer Portal.
      </p>
      <div className="mt-4">
        <Link to="/app/people" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
          Manage workspace people <ExternalLink size={14} />
        </Link>
      </div>
    </Card>
  );
}

export default function CustomersApp() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [success] = useState('');
  return (
    <div className="space-y-6">
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}
      {openId ? (
        <CustomerDetailView id={openId} onBack={() => setOpenId(null)} />
      ) : (
        <>
          <CustomerList onOpen={setOpenId} />
          <PeopleSettings />
        </>
      )}
    </div>
  );
}
